import { useEffect, useState } from "react";
import { API } from "../App";
import { Button } from "./ui/button";
import { ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "";

/**
 * Cobranding card shown on the customer home page, below the wallet balance.
 * Renders only if `/api/cobranding/me` returns at least one active entry matching
 * the user's customer segments (or with no segment restriction).
 */
export default function CobrandingCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/cobranding/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => alive && setItems(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="cobranding-card-wrapper">
      {items.map((c) => (
        <article
          key={c.cobranding_id}
          className="relative overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm"
          data-testid={`cobranding-card-${c.cobranding_id}`}
        >
          {c.banner_url && (
            <div className="w-full aspect-[16/7] bg-gray-50 dark:bg-gray-800 overflow-hidden">
              <img
                src={resolveUrl(c.banner_url)}
                alt={c.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          )}
          <div className="p-4 flex items-start gap-3">
            {c.logo_url && (
              <div className="w-12 h-12 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                <img
                  src={resolveUrl(c.logo_url)}
                  alt=""
                  className="w-full h-full object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 dark:text-white text-sm break-words leading-snug">{c.name}</h3>
              {c.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words leading-snug">
                  {c.description}
                </p>
              )}
              {c.url && (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-green-700 dark:text-emerald-300 hover:text-green-800"
                  data-testid={`cobranding-url-${c.cobranding_id}`}
                >
                  Learn more <ArrowRight size={10} />
                </a>
              )}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
